import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { certificateAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';
import QRCode from 'qrcode.react';
import toast from 'react-hot-toast';

const CertificateDetails = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    fetchCertificate();
  }, [id]);

  const fetchCertificate = async () => {
    try {
      setLoading(true);
      const response = await certificateAPI.getCertificateById(id);
      setCertificate(response.data.data.certificate);
    } catch (error) {
      console.error('Failed to fetch certificate:', error);
      toast.error('Failed to load certificate details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      MINTED: 'badge-success',
      PENDING: 'badge-warning',
      REVOKED: 'badge-error',
      VERIFIED: 'badge-success'
    };
    return `badge ${statusClasses[status] || 'badge-info'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const shareData = {
      title: `Academic Record - ${certificate.studentData?.name}`,
      text: `${certificate.academicData?.degree} from ${certificate.college?.name}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById('qr-code');
    const url = canvas.toDataURL();
    const link = document.createElement('a');
    link.download = `verification-qr-${certificate._id}.png`;
    link.href = url;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <h2>Record Not Found</h2>
          <Link to="/" className="btn btn-primary">Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="certificate-details-page">
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Digital Academic Record
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>ID: {certificate._id}</p>
          </div>

          <div className="flex gap-2">
            <button onClick={handleShare} className="btn btn-secondary">Share</button>
            <button onClick={handlePrint} className="btn btn-secondary">Print</button>
            <Link to={`/verify/${certificate._id}`} className="btn btn-primary">Verify Accuracy</Link>
          </div>
        </div>

        <div className="certificate-container mb-8">
          <div className="certificate">
            <div className="certificate-header">
              <h2 className="certificate-title">Certificate of Achievement</h2>
              <p className="certificate-subtitle">{certificate.college?.name}</p>
            </div>

            <div className="certificate-body">
              <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Awarded to</p>
              <div className="certificate-student">{certificate.studentData?.name}</div>
              <p style={{ fontSize: '1.1rem', margin: '1rem 0' }}>for the successful completion of</p>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-color)', marginBottom: '2rem' }}>
                {certificate.academicData?.degree}
              </div>

              <div className="certificate-details">
                <div className="certificate-field">
                  <div className="certificate-field-label">Student ID</div>
                  <div className="certificate-field-value">{certificate.studentData?.studentId}</div>
                </div>
                <div className="certificate-field">
                  <div className="certificate-field-label">Graduation Year</div>
                  <div className="certificate-field-value">{certificate.academicData?.graduationYear}</div>
                </div>
                <div className="certificate-field">
                  <div className="certificate-field-label">CGPA</div>
                  <div className="certificate-field-value">{certificate.academicData?.overallCGPA} / 10</div>
                </div>
                <div className="certificate-field">
                  <div className="certificate-field-label">Classification</div>
                  <div className="certificate-field-value">{certificate.academicData?.classification}</div>
                </div>
              </div>
            </div>

            <div className="certificate-footer">
              <p>Issued on: {formatDate(certificate.issuedAt)}</p>
              <p>Blockchain Hash: {certificate.blockchain?.certificateHash?.substring(0, 20)}...</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Blockchain Evidence</h3></div>
            <div className="card-body">
              <div className="mb-4">
                <span className={getStatusBadge(certificate.status)}>{certificate.status}</span>
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                <p><strong>NFT Token ID:</strong> #{certificate.tokenId || 'Pending'}</p>
                <p><strong>Network:</strong> Ethereum / Polygon (Mock)</p>
                <p style={{ wordBreak: 'break-all' }}><strong>TX Hash:</strong> {certificate.blockchain?.transactionHash}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h3 className="card-title">One-Tap Verification</h3></div>
            <div className="card-body text-center">
              <div className="qr-container">
                <QRCode
                  id="qr-code"
                  value={`${window.location.origin}/verify/${certificate._id}`}
                  size={150}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <button onClick={downloadQR} className="btn btn-sm btn-secondary mt-4">Download QR</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateDetails;